// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Menu, ArrowRight, Calendar, LayoutDashboard, LogIn, ChevronDown, MessageSquare, Calculator, PlayCircle } from "lucide-react";
import { Link, useLocation } from "wouter";
import BookingModal from "../components/BookingModal";

const AI_TOOLS_DROPDOWN = [
  { name: 'AI Chat Widget',        href: '/ai-chat-widget',           icon: MessageSquare, desc: 'Smart chat for your site' },
  { name: 'Instant Quote Widget',  href: '/instant-quote-widget',     icon: Calculator,    desc: 'Quote forms that convert' },
  { name: 'Live Demos',            href: '/demos',                    icon: PlayCircle,    desc: 'See it working live' },
];

const navigation = [
  { name: 'Home',        href: '/' },
  { name: 'Services',    href: '/services' },
  { name: 'AI Tools',    href: '/ai-tools', hasDropdown: true },
  { name: 'Blog',        href: '/blog' },
  { name: 'Free Audit',  href: '/free-website-audit', badge: 'FREE' },
  { name: 'Contact',     href: '/contact' },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [aiToolsOpen, setAiToolsOpen] = useState(false);
  const [mobileAiOpen, setMobileAiOpen] = useState(false);
  const closeTimer = useRef(null);
  const [location] = useLocation();

  const { data: demosData } = useQuery({
    queryKey: ["/api/demos/"],
    staleTime: 1000 * 60 * 5,
  });

  // Check if the user has an active widget-client Django session
  const { data: authData } = useQuery({
    queryKey: ["/api/widget-auth-status/"],
    staleTime: 1000 * 60 * 2,
    retry: false,
  });
  const isWidgetLoggedIn = authData?.authenticated === true;
  const dashboardUrl = authData?.dashboard_url || '/widget-dashboard/';

  const openDropdown = () => {
    clearTimeout(closeTimer.current);
    setAiToolsOpen(true);
  };
  const closeDropdown = () => {
    closeTimer.current = setTimeout(() => setAiToolsOpen(false), 120);
  };

  const isAiToolsActive = ['/ai-tools', '/ai-chat-widget', '/instant-quote-widget', '/demos'].includes(location);

  return (
    <header className="bg-white sticky top-0 z-50 border-t-0 border-b border-gray-100">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link href="/" aria-label="Codeteki Home">
            <img
              src="/static/images/navbar-logo.png"
              alt="Codeteki - AI Business Solutions"
              className="h-12 w-auto cursor-pointer transition-transform duration-300 hover:scale-110"
              loading="eager"
              decoding="async"
              width="93"
              height="48"
              fetchpriority="high"
            />
          </Link>

          {/* Desktop nav */}
          <nav id="navigation" className="hidden md:flex items-center space-x-6">
            {navigation.map((item, index) => {
              if (item.hasDropdown) {
                return (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={openDropdown}
                    onMouseLeave={closeDropdown}
                  >
                    <Link href={item.href}>
                      <button
                        className={`relative flex items-center gap-1 font-medium transition-all duration-300 transform hover:scale-105 group ${isAiToolsActive ? 'text-[#f9cb07]' : 'text-gray-700 hover:text-[#f9cb07]'}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {item.name}
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${aiToolsOpen ? 'rotate-180' : ''}`} />
                        <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#f9cb07] to-[#ffcd3c] transition-all duration-300 ${isAiToolsActive ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                      </button>
                    </Link>

                    {/* Dropdown panel */}
                    {aiToolsOpen && (
                      <div
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50"
                        onMouseEnter={openDropdown}
                        onMouseLeave={closeDropdown}
                      >
                        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45" />
                        {AI_TOOLS_DROPDOWN.map((sub) => (
                          <Link key={sub.name} href={sub.href}>
                            <button
                              onClick={() => setAiToolsOpen(false)}
                              className={`flex items-start gap-3 w-full px-4 py-2.5 text-left hover:bg-yellow-50 transition-colors group ${location === sub.href ? 'bg-yellow-50' : ''}`}
                            >
                              <sub.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${location === sub.href ? 'text-[#f9cb07]' : 'text-gray-400 group-hover:text-[#f9cb07]'}`} />
                              <div>
                                <div className={`text-sm font-medium ${location === sub.href ? 'text-[#d4a900]' : 'text-gray-800 group-hover:text-gray-900'}`}>{sub.name}</div>
                                <div className="text-xs text-gray-500">{sub.desc}</div>
                              </div>
                            </button>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link key={item.name} href={item.href}>
                  <button
                    className={`relative flex items-center gap-1.5 font-medium transition-all duration-300 transform hover:scale-105 group ${location === item.href ? 'text-[#f9cb07]' : 'text-gray-700 hover:text-[#f9cb07]'}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {item.name}
                    {item.badge && (
                      <span className="text-[10px] font-bold bg-[#f9cb07] text-black px-1.5 py-0.5 rounded-full leading-none">
                        {item.badge}
                      </span>
                    )}
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#f9cb07] to-[#ffcd3c] transition-all duration-300 ${location === item.href ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                  </button>
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTAs */}
          <div className="flex items-center space-x-3">
            {isWidgetLoggedIn ? (
              <a
                href={dashboardUrl}
                className="hidden md:inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold text-slate-900 transition-all duration-200 hover:shadow-sm"
                style={{ background: "#f9cb07" }}
              >
                <LayoutDashboard className="w-4 h-4" />
                My Dashboard
              </a>
            ) : (
              <a
                href="/client-login/"
                className="hidden md:inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:border-[#f9cb07] hover:text-gray-900 transition-all duration-200"
              >
                <LogIn className="w-4 h-4" />
                Client Login
              </a>
            )}
            <Button
              onClick={() => setBookingOpen(true)}
              className="group bg-gradient-to-r from-[#f9cb07] to-[#ffcd3c] hover:from-[#e6b800] hover:to-[#f9cb07] text-black font-semibold hidden md:inline-flex transition-all duration-300 transform hover:scale-105 hover:shadow-lg btn-animated"
            >
              <Calendar className="w-4 h-4 mr-2" />
              <span className="transform group-hover:translate-x-1 transition-transform duration-300">Book Call</span>
              <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-300" />
            </Button>

            {/* Mobile hamburger */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation menu">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col space-y-1 mt-8">
                  {navigation.map((item) => {
                    if (item.hasDropdown) {
                      return (
                        <div key={item.name}>
                          <button
                            onClick={() => setMobileAiOpen(v => !v)}
                            className="flex items-center justify-between w-full text-left text-lg font-medium text-gray-700 hover:text-[#f9cb07] transition-colors py-2"
                          >
                            {item.name}
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAiOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {mobileAiOpen && (
                            <div className="ml-4 mb-2 flex flex-col space-y-1 border-l-2 border-yellow-200 pl-3">
                              {AI_TOOLS_DROPDOWN.map((sub) => (
                                <Link key={sub.name} href={sub.href}>
                                  <button
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2 w-full text-left text-base text-gray-600 hover:text-[#f9cb07] transition-colors py-1.5"
                                  >
                                    <sub.icon className="w-4 h-4 text-gray-400" />
                                    {sub.name}
                                  </button>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <Link key={item.name} href={item.href}>
                        <button
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-2 text-left text-lg font-medium text-gray-700 hover:text-[#f9cb07] transition-colors w-full py-2"
                        >
                          {item.name}
                          {item.badge && (
                            <span className="text-[10px] font-bold bg-[#f9cb07] text-black px-1.5 py-0.5 rounded-full leading-none">
                              {item.badge}
                            </span>
                          )}
                        </button>
                      </Link>
                    );
                  })}

                  <div className="pt-4 flex flex-col space-y-3">
                    <Button
                      className="bg-gradient-to-r from-[#f9cb07] to-[#ffcd3c] hover:from-[#e6b800] hover:to-[#f9cb07] text-black font-semibold"
                      onClick={() => { setBookingOpen(true); setIsOpen(false); }}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Call
                    </Button>
                    {isWidgetLoggedIn ? (
                      <a
                        href={dashboardUrl}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2 text-left text-base font-semibold rounded-lg px-4 py-2.5 transition-colors"
                        style={{ background: "#f9cb07", color: "#0f172a" }}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        My Dashboard
                      </a>
                    ) : (
                      <a
                        href="/client-login/"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2 text-left text-base font-semibold text-gray-700 border border-gray-200 rounded-lg px-4 py-2.5 hover:border-[#f9cb07] transition-colors"
                      >
                        <LogIn className="w-4 h-4" />
                        Client Login
                      </a>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
    </header>
  );
}
